import clsx from 'clsx'

import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { localeUrl } from 'helpers/utils'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { ResultQuickAddOverlay } from 'libs/searchspring/components/ResultQuickAddOverlay'
import { ResultQuickAdd } from 'libs/searchspring/components/ResultQuickAdd'

const DEFAULT_OPTIONS = {
  hideQuickAdd: false,
  hideQuickAddModal: true,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0', eager = false, imageSizes, imageWidths }) {
  const [variant] = result.variants

  const finalPrice = variant.price
  const finalCompareAtPrice = variant.compare_at_price

  const {
    hideQuickAdd = DEFAULT_OPTIONS.hideQuickAdd,
    hideQuickAddModal = DEFAULT_OPTIONS.hideQuickAddModal,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges,
    hideWishlist = DEFAULT_OPTIONS.hideWishlist
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const displayName = currentResult.mfield_utopia_customer_facing_product_name || currentResult.name

  const criteria = useMemo(() => ({
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle],
    location: 'PLP'
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  // Metaobject variables
  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return localeUrl(`products/${currentResult.handle}`)
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card grow relative flex size-full', className)} id={`result__${currentResult.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={dataIndex}>
      <div className="flex flex-col w-full bg-secondary outline-card">
        <div className="relative w-full overflow-hidden aspect-2/3">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult} eager={eager} sizes={imageSizes} widths={imageWidths}>
              {!hideBadges && (firstBadge || secondBadge) && (
                <div className="absolute top-0.625 left-0.625 md:top-1 md:left-1 flex flex-col gap-y-0.375 max-w-[80%]">
                  {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                  {secondBadge && <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />}
                </div>
              )}
            </ResultImage>
          </a>
          {/* Quick Add */}
          {(!hideQuickAdd && currentResult.template_suffix !== 'gift-card') && (
            <div className="hidden lg:block absolute bottom-0.625 lg:bottom-1.5 right-0.625 lg:right-1.5 w-fit lg:w-[calc(100%-3rem)]">
              <ResultQuickAddOverlay result={currentResult} />
            </div>
          )}
          {/* WishlistKing Button */}
          {(!hideWishlist && currentResult.template_suffix !== 'gift-card') && (
            <div className="wishlist-button flex items-center justify-center absolute top-0 md:top-0.625 right-0 lg:right-0.625 w-[36px] lg:w-[24px] aspect-1/1 z-2">
              <button type="button"
                data-product-handle={currentResult.handle}
                aria-label="Add to Wishlist"
                className="wkh-button save-wishlist link-fade w-[8px] h-[11px] children:size-full"
              >
                <svg class="wkh-icon" xmlns="http://www.w3.org/2000/svg" width="9" height="13" viewBox="0 0 9 13" fill="none">
                  <path d="M8.5 12L4.5 9.5L0.5 12V1C0.5 0.867392 0.552679 0.740215 0.646447 0.646447C0.740215 0.552679 0.867392 0.5 1 0.5H8C8.13261 0.5 8.25979 0.552679 8.35355 0.646447C8.44732 0.740215 8.5 0.867392 8.5 1V12Z" fill="var(--icon-fill)" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
        <div
          className={clsx(
            'grow flex flex-col p-1 lg:p-1.5',
            !hideQuickAddModal && 'pb-0.5'
          )}
        >
          <div className="flex lg-max:flex-col lg:justify-between gap-x-0.75 gap-y-0.125">
            <a href={url} className="paragraph-xs font-heading-spaced uppercase link-fade" aria-label={displayName} dangerouslySetInnerHTML={{ __html: displayName }} />
            <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} className="flex lg:flex-col gap-x-0.25 lg:text-right" />
          </div>

          <div className="flex lg-max:flex-col lg:justify-between gap-x-0.75">
            {!hideSwatches && (
              <div className="lg:grow flex flex-wrap mt-0.25 lg:mt-0.5 empty:!mt-0">
                <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />
              </div>
            )}
            {!hidePromolines && firstPromoline ? (
              <div className="flex mt-0.25 lg:mt-0.75 text-right">
                {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
              </div>
            ) : (
              <klaviyo-star-rating
                product-id={currentResult.uid}
                product-title={currentResult.name}
                product-type={currentResult.type}
                className="mt-0.25 lg:mt-0.75"
              >
              </klaviyo-star-rating>
            )}
          </div>

          {(!hideQuickAddModal && currentResult.template_suffix !== 'gift-card') && (
            <div className="grow flex lg:hidden items-end mt-0.25 lg:mt-0.5">
              <ResultQuickAdd result={currentResult} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
